import moment from 'moment';
import { useEffect, useRef } from 'react';
import { formatNumber, toReportInputs } from '../../../../../utils/format';
import useToast from '../../../../../hooks/useToast';
import { useLazyGetRetentionQuery } from '@api/front-office/profile.api.rtk';
import FormPDF, { IFormInput, IFormPDFRef } from '@components/pdf/form-pdf';
import { TableRow } from '@components/pdf/react-pdf/react-pdf.interface';
import useAppContext from '../../../../../hooks/useAppContext';

export const PDFGuestDataForm = ({
  profileID = '',
  includeCollections,
  isOpen = false,
  handleModalClose,
}: {
  profileID?: string;
  includeCollections: boolean;
  isOpen: boolean;
  handleModalClose?: () => void;
}) => {
  const { startLoading, stopLoading } = useAppContext();
  const { toast } = useToast();
  const formRef = useRef<IFormPDFRef>(null);
  const [getGuestDataDetails] = useLazyGetRetentionQuery();

  const handlePrint = async ({
    profileID,
    includeCollections,
  }: {
    profileID: string;
    includeCollections: boolean;
  }) => {
    try {
      startLoading();
      if (!formRef.current) {
        toast({ severity: 'warning', message: 'No PDF Component Found' });
        return;
      }

      const { getHeaderDetails, getPropertyLogo, handleShowPDF, getTemplate } =
        formRef.current;

      const template = await getTemplate({
        department: 'FO',
        category: 'Reservations',
        templateName: 'Guest Data',
      });

      if (!template) return;

      const data = await getGuestDataDetails({
        profileID,
        includeCollections,
      })
        .unwrap()
        .then((result) => result?.[0]?.guestData);

      const { propertyLogo } = await getPropertyLogo();
      const headerDetails = getHeaderDetails({ moduleName: 'Xenia FOS' });

      const reservationDetails: TableRow[] = data?.bookings
        ? data.bookings.map(
            (item): TableRow => ({
              cells: [
                { value: item.bookingID?.toString() ?? '', id: 'bookingID' },
                {
                  value: `${(moment(item?.arrival).format('MMM DD, YYYY') ?? '').trim() || '-'}`,
                  id: 'arrival',
                },
                {
                  value: `${(moment(item?.departure).format('MMM DD, YYYY') ?? '').trim() || '-'}`,
                  id: 'departure',
                },
                {
                  value: `${(item?.room ?? '').trim() || '-'}`,
                  id: 'room',
                },
                {
                  value: `${(item?.pax ?? '').trim() || '-'}`,
                  id: 'pax',
                },
                {
                  value: `${(item?.sharers ?? '').toString().trim() || '-'}`,
                  id: 'sharers',
                },
                {
                  value: `${(formatNumber(item?.payables, 2, 2) ?? '').toString().trim() || '-'}`,
                  id: 'payables',
                },
                {
                  value: `${(formatNumber(item?.collections, 2, 2) ?? '').toString().trim() || '-'}`,
                  id: 'collections',
                },
                {
                  value: `${(formatNumber(item?.balance, 2, 2) ?? '').toString().trim() || '-'}`,
                  id: 'balance',
                },
              ],
            }),
          )
        : [];

      const collections: TableRow[] = data?.ledger
        ? data.ledger.map(
            (item): TableRow => ({
              cells: [
                { value: `${(item?.resID ?? '').trim() || '-'}`, id: 'resID' },
                {
                  value: `${(moment(item?.date).format('MMM DD, YYYY') ?? '').trim() || '-'}`,
                  id: 'date',
                },
                { value: `${(item?.item ?? '').trim() || '-'}`, id: 'item' },
                { value: `${(item?.type ?? '').trim() || '-'}`, id: 'type' },
                {
                  value: `${(formatNumber(item?.amount, 2, 2) ?? '').toString().trim() || '-'}`,
                  id: 'amount',
                },
                {
                  value: `${(item?.remarks ?? '').trim() || '-'}`,
                  id: 'remarks',
                },
              ],
            }),
          )
        : [];

      const inputs: IFormInput = {
        ...(propertyLogo && { propertyLogo }),
        ...headerDetails,
        ...toReportInputs(data ?? {}),
        ...(reservationDetails?.length > 0
          ? { reservationDetails }
          : { reservationDetails: [] }),
        ...(collections?.length > 0
          ? { collections }
          : { collections: [], collectionsTitle: '' }),
      };

      handleShowPDF({
        template,
        inputs,
        hideEmptyContent: true,
      });
    } catch (error) {
      handleModalClose?.();
      toast({ message: error, severity: 'warning' });
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (!isOpen || !profileID) return;

    (async () => await handlePrint({ profileID, includeCollections }))();
  }, [isOpen, profileID, includeCollections]);

  return (
    <FormPDF
      {...{
        ref: formRef,
        title: 'Guest Information',
        modalOnClose: handleModalClose,
      }}
    />
  );
};
